/* Ember gift engine — which free-gift lines a cart SHOULD contain.
 *
 * Pure functions, no DOM, no network. Shipped to the theme as
 * assets/gift-engine.js (plain script → window.EmberGiftEngine) and unit-tested
 * in the app repo next to extensions/gift-tiers-discount, whose pricing it must
 * agree with: every gift this engine asks for must be one the discount function
 * frees (see gift-engine.test.ts "parity").
 *
 * Config = the tier config the theme renders from shop metafield
 * spectrum.gift_tiers ([data-spend-gift-config]):
 *   { mode: 'auto'|'claim'|'checkout', exclusive: bool,
 *     tiers: [{ threshold (₹), variantId (number), price (₹, 0 = free),
 *               available (bool, optional), ...display fields }],
 *     excludedProducts: [handle], excludedVariants: [variant id],
 *     excludedVariantIds: [variant id]  ← every variant of excludedProducts,
 *                                         resolved by the theme (adds only know ids) }
 *     productGift: { enabled, variantId, products: [handle], variants: [id],
 *                    variantHandles: { variantId: handle } ← resolved by the theme }
 * Excluded lines never count toward a tier (same rule as the discount function).
 * While the product gift applies (a listed product in the cart, offer on), tier
 * gifts are OFF for the whole cart — same rule as the discount function.
 * Product gift: one free unit of productGift.variantId per UNIT of a listed product
 * in the cart, tagged __product_gift — separate from the tiers.
 *
 * Cart = Shopify AJAX cart shape (/cart.js): { total_price, items: [{ key,
 * variant_id, quantity, final_line_price, properties }] } — money in paise.
 */
(function (root) {
  'use strict';

  var TIER_TAG = '__gift_tier';
  // Any of these marks a line as a gift: it never counts toward the threshold.
  var PRODUCT_TAG = '__product_gift';
  var GIFT_TAGS = ['__gift_tier', '__spend_gift', '__bxgy_gift', '__gift_rule', PRODUCT_TAG];

  function isGiftLine(item) {
    var p = (item && item.properties) || {};
    for (var i = 0; i < GIFT_TAGS.length; i++) if (p[GIFT_TAGS[i]]) return true;
    return false;
  }
  function isTierGift(item) {
    return !!(item && item.properties && item.properties[TIER_TAG]);
  }
  function rupeesToPaise(r) {
    var n = Number(r);
    return Number.isFinite(n) ? Math.round(n * 100) : NaN;
  }

  function idSet(list) {
    var set = {};
    (Array.isArray(list) ? list : []).forEach(function (v) {
      var m = String(v == null ? '' : v).match(/(\d+)\s*$/);
      if (m) set[m[1]] = true;
    });
    return set;
  }
  function handleSet(list) {
    var set = {};
    (Array.isArray(list) ? list : []).forEach(function (h) {
      var k = String(h == null ? '' : h).trim().toLowerCase();
      if (k) set[k] = true;
    });
    return set;
  }
  /* Is this variant (and optionally product handle) excluded from the tiers? */
  function isExcluded(variantId, handle, config) {
    if (!config) return false;
    var vid = String(variantId == null ? '' : variantId);
    // A product listed in productGift is excluded ONLY while that offer is on
    // (wins over excludedProducts) — switching the offer off restores it to the bar.
    var pg = config.productGift;
    if (pg) {
      var ph = String(handle == null ? '' : handle).toLowerCase();
      if (!ph && pg.variantHandles) ph = String(pg.variantHandles[vid] || '').toLowerCase();
      if ((ph && handleSet(pg.products)[ph]) || (vid && idSet(pg.variants)[vid])) return pg.enabled !== false;
    }
    if (vid && (idSet(config.excludedVariants)[vid] || idSet(config.excludedVariantIds)[vid])) return true;
    var h = String(handle == null ? '' : handle).toLowerCase();
    return !!h && !!handleSet(config.excludedProducts)[h];
  }

  /* Paid total the tiers are measured on, in paise: the cart total minus every
   * gift line and every excluded line. Post-discount, same basis the cart bar
   * shows. (The discount function measures pre-discount, which is always >= this,
   * and frees the highest gift PRESENT whose threshold is met — so a gift chosen
   * here is always one it frees.) */
  function paidPaise(cart, config) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var skip = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftLine(it) || isExcluded(it.variant_id, it.handle, config)) skip += it.final_line_price || 0;
    }
    return Math.max(0, (cart.total_price || 0) - skip);
  }

  function validTiers(config) {
    if (!config || !Array.isArray(config.tiers)) return [];
    return config.tiers.filter(function (t) {
      return t && Number(t.variantId) > 0 && rupeesToPaise(t.threshold) > 0;
    });
  }

  /* Variant ids of the tier gifts a cart with this paid total should hold.
   * Exclusive (default): only the highest reached tier. Cumulative: every
   * reached tier. An unavailable earned gift yields nothing for that tier —
   * never a lower-tier substitute (the function would not free it in exclusive
   * mode, so the shopper would be charged). Only 'auto' mode adds gifts. */
  function desiredTierGifts(paid, config) {
    if (!config || (config.mode || 'auto') !== 'auto') return [];
    var reached = validTiers(config).filter(function (t) { return paid >= rupeesToPaise(t.threshold); });
    if (!reached.length) return [];
    if (config.exclusive !== false) {
      var top = reached[0];
      for (var i = 1; i < reached.length; i++) {
        if (rupeesToPaise(reached[i].threshold) > rupeesToPaise(top.threshold)) top = reached[i];
      }
      reached = [top];
    }
    return reached
      .filter(function (t) { return t.available !== false; })
      .map(function (t) { return Number(t.variantId); });
  }

  /* What to change so `cart` holds exactly the desired tier gifts.
   *   add:    variant ids to add (qty 1, tagged)
   *   remove: line keys to set to 0 (not desired, or duplicate lines)
   *   clamp:  line keys to set to 1 (a desired gift line with qty > 1)
   * Keys come from the cart passed in — callers must pass the LIVE cart. */
  function planTierGifts(cart, config) {
    var plan = { add: [], remove: [], clamp: [] };
    if (!cart || !Array.isArray(cart.items) || !config) return plan;
    var mode = config.mode || 'auto';
    var locked = productGiftActive(cart, config);
    var paid = paidPaise(cart, config);
    var desired = locked ? [] : desiredTierGifts(paid, config);
    var thresholdByVid = {};
    validTiers(config).forEach(function (t) { thresholdByVid[Number(t.variantId)] = rupeesToPaise(t.threshold); });
    var kept = {};
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!isTierGift(it)) continue;
      var vid = Number(it.variant_id);
      var keep = locked ? false : mode === 'auto'
        ? desired.indexOf(vid) !== -1 && !kept[vid]
        // claim / checkout: a claimed gift stays while its own tier still holds
        : thresholdByVid[vid] != null && paid >= thresholdByVid[vid] && !kept[vid];
      if (!keep) { plan.remove.push(it.key); continue; }
      kept[vid] = true;
      if (it.quantity > 1) plan.clamp.push(it.key);
    }
    for (var d = 0; d < desired.length; d++) if (!kept[desired[d]]) plan.add.push(desired[d]);
    return plan;
  }

  /* Gift line items to send in the SAME /cart/add.js, given the cart before the
   * add and the paid (non-gift, NON-EXCLUDED — callers skip isExcluded items)
   * paise being added. Only gifts
   * that are newly earned and not already present — swaps/removals are left to
   * planTierGifts after the add lands. */
  function giftsForAdd(cartBefore, addingPaise, config, addingItems) {
    if (!cartBefore || !Array.isArray(cartBefore.items)) return [];
    if (productGiftActive(cartBefore, config, addingItems)) return []; // set offer applies → no tier gift
    for (var i = 0; i < cartBefore.items.length; i++) if (isTierGift(cartBefore.items[i])) return [];
    var desired = desiredTierGifts(paidPaise(cartBefore, config) + Math.max(0, addingPaise || 0), config);
    return desired.map(giftLineItem);
  }

  /* ---------- product gift (separate offer) ---------- */
  function productGiftConfig(config) {
    var pg = config && config.productGift;
    if (!pg || pg.enabled === false || !(Number(pg.variantId) > 0)) return null;
    return pg;
  }
  function isProductGift(item) {
    return !!(item && item.properties && item.properties[PRODUCT_TAG]);
  }
  /* Trigger key (the variant id) of a paid line whose product or variant is
   * listed, or null. */
  function productTrigger(variantId, handle, pg) {
    var vid = String(variantId == null ? '' : variantId);
    var h = String(handle == null ? '' : handle).toLowerCase();
    if (!h && pg.variantHandles) h = String(pg.variantHandles[vid] || '').toLowerCase();
    if ((h && handleSet(pg.products)[h]) || (vid && idSet(pg.variants)[vid])) return vid || h;
    return null;
  }
  /* Free units the cart has earned: one per UNIT of a listed product (non-gift
   * lines), plus the `adding` lines ([{ id, quantity, handle? }]). */
  function productGiftsEarned(cart, config, adding) {
    var pg = productGiftConfig(config);
    if (!pg) return 0;
    var n = 0;
    var consider = function (vid, handle, qty) {
      if (productTrigger(vid, handle, pg)) n += parseInt(qty, 10) || 1;
    };
    ((cart && cart.items) || []).forEach(function (it) { if (!isGiftLine(it)) consider(it.variant_id, it.handle, it.quantity); });
    (adding || []).forEach(function (it) { if (!(it.properties && isGiftLine(it))) consider(it.id, it.handle, it.quantity); });
    return n;
  }
  /* Does the set offer apply (→ tier gifts off)? `adding` = lines about to be added. */
  function productGiftActive(cart, config, adding) {
    return productGiftsEarned(cart, config, adding) > 0;
  }
  /* Product-gift lines needed: { add: qty to add, updates: { key: qty } }. */
  function planProductGifts(cart, config) {
    var plan = { add: 0, updates: {} };
    var pg = productGiftConfig(config);
    var want = productGiftsEarned(cart, config);
    var have = 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (!isProductGift(it)) return;
      var ok = pg && Number(it.variant_id) === Number(pg.variantId);
      var keep = ok ? Math.max(0, Math.min(it.quantity, want - have)) : 0;
      if (keep !== it.quantity) plan.updates[it.key] = keep;
      have += keep;
    });
    if (pg && want > have) plan.add = want - have;
    return plan;
  }
  function productGiftItem(qty, config) {
    var props = {};
    props[PRODUCT_TAG] = '1';
    return { id: Number(productGiftConfig(config).variantId), quantity: qty, properties: props };
  }
  /* Product-gift line items to send in the SAME /cart/add.js as `adding`. */
  function productGiftsForAdd(cartBefore, adding, config) {
    if (!productGiftConfig(config) || !cartBefore || !Array.isArray(cartBefore.items)) return [];
    var have = 0;
    cartBefore.items.forEach(function (it) { if (isProductGift(it)) have += it.quantity; });
    var extra = productGiftsEarned(cartBefore, config, adding) - have;
    return extra > 0 ? [productGiftItem(extra, config)] : [];
  }

  function giftLineItem(variantId) {
    var props = {};
    props[TIER_TAG] = '1';
    return { id: Number(variantId), quantity: 1, properties: props };
  }

  function isNoop(plan) {
    return !plan || (!plan.add.length && !plan.remove.length && !plan.clamp.length);
  }
  function isProductNoop(plan) {
    return !plan || (!plan.add && !Object.keys(plan.updates).length);
  }

  var api = {
    TIER_TAG: TIER_TAG,
    GIFT_TAGS: GIFT_TAGS.slice(),
    isGiftLine: isGiftLine,
    isTierGift: isTierGift,
    isExcluded: isExcluded,
    paidPaise: paidPaise,
    desiredTierGifts: desiredTierGifts,
    planTierGifts: planTierGifts,
    giftsForAdd: giftsForAdd,
    giftLineItem: giftLineItem,
    isNoop: isNoop,
    PRODUCT_TAG: PRODUCT_TAG,
    isProductGift: isProductGift,
    productGiftsEarned: productGiftsEarned,
    productGiftActive: productGiftActive,
    planProductGifts: planProductGifts,
    productGiftItem: productGiftItem,
    productGiftsForAdd: productGiftsForAdd,
    isProductNoop: isProductNoop,
  };
  root.EmberGiftEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
